import React, { useState, useContext } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  IconButton,
  Grid,
  Chip,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { ArrowBack, Code, School, Person } from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import axios from '../../api/axios';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { GridScan } from '../../components/GridScan/GridScan';

function Login() {
  const { login } = useContext(AuthContext);
  const { t } = useTranslation();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('student');
  const [accessCode, setAccessCode] = useState('');
  const [isRegister, setIsRegister] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = { phoneNumber };
      
      // 教师登录/注册都需要访问码
      if (role === 'teacher') {
        if (!accessCode) {
          setError(t('login.errors.accessCodeRequired'));
          setLoading(false);
          return;
        }
        payload.accessCode = accessCode;
      }
      
      if (isRegister) {
        if (!name) {
          setError(t('login.errors.nameRequired'));
          setLoading(false);
          return;
        }
        payload.name = name;
        payload.role = role;
      }

      console.log('发送的数据:', payload); // 调试日志
      const response = await axios.post('/auth/login', payload);
      login(response.data.user, response.data.token);
    } catch (err) {
      // Handle SSL protocol errors
      if (err.code === 'ERR_SSL_PROTOCOL_ERROR' || err.message?.includes('SSL') || err.message?.includes('protocol')) {
        setError('网络连接错误：SSL 协议错误。请确保前端和后端使用相同的协议（HTTP 或 HTTPS）。如果通过 HTTPS 访问，请确保后端也支持 HTTPS。');
        console.error('[Login] SSL Error:', err);
        console.error('[Login] Current URL:', window.location.href);
        console.error('[Login] API Base URL:', err.config?.baseURL);
      } else if (err.response?.status === 400 && err.response?.data?.error?.includes('Name is required')) {
        setIsRegister(true);
        setError(t('login.errors.newUser'));
      } else if (err.response?.status === 403) {
        setError(t('login.errors.invalidAccessCode'));
      } else {
        setError(err.userMessage || err.response?.data?.error || err.message || t('login.errors.operationFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchMode = () => {
    setIsRegister(!isRegister);
    setError('');
    setName('');
    setAccessCode('');
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        bgcolor: "#f5f5f5",
        position: "relative",
      }}
    >
      <Grid container sx={{ height: "100vh" }}>
        {/* Left Side - Grid Scan Background (更大比例: ~58%) */}
        <Grid
          item
          xs={false}
          md={7}
          sx={{
            position: "relative",
            display: { xs: "none", md: "flex" },
            backgroundColor: "#000000",
            overflow: "hidden",
          }}
        >
          {/* Grid Scan Background */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 0,
            }}
          >
            <GridScan
              sensitivity={0.55}
              lineThickness={1}
              linesColor="#392e4e"
              gridScale={0.1}
              scanColor="#FF9FFC"
              scanOpacity={0.4}
              enablePost
              bloomIntensity={0.6}
              chromaticAberration={0.002}
              noiseIntensity={0.01}
            />
          </Box>

          {/* Overlay with content */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: "rgba(0, 0, 0, 0.3)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              color: "white",
              p: 4,
              zIndex: 1,
            }}
          >
            <Code sx={{ fontSize: 80, mb: 3, opacity: 0.9 }} />
            <Typography
              variant="h6"
              sx={{ opacity: 0.95, textAlign: "center", maxWidth: 400, mt: 2 }}
            >
              我来速成只做三件事
            </Typography>
            <Typography
              variant="h6"
              sx={{ opacity: 0.95, textAlign: "center", maxWidth: 400, mt: 2 }}
            >
              学习
            </Typography>
            <Typography
              variant="h6"
              sx={{ opacity: 0.95, textAlign: "center", maxWidth: 400, mt: 2 }}
            >
              学习
            </Typography>
            <Typography
              variant="h6"
              sx={{ opacity: 0.95, textAlign: "center", maxWidth: 400, mt: 2 }}
            >
              还是TMD学习
            </Typography>
            <Box
              sx={{
                mt: 4,
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <Chip
                label="Python"
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  fontWeight: "bold",
                }}
              />
              <Chip
                label="算法"
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  fontWeight: "bold",
                }}
              />
              <Chip
                label="数据结构"
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  fontWeight: "bold",
                }}
              />
            </Box>
          </Box>
        </Grid>

        {/* Right Side - Form (较小比例: ~42%) */}
        <Grid
          item
          xs={12}
          md={5}
          component={Paper}
          elevation={0}
          square
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            p: 4,
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 450,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Logo for mobile */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                justifyContent: "center",
                mb: 3,
              }}
            >
              <Code sx={{ fontSize: 60, color: "primary.main" }} />
            </Box>

            {/* Mode Toggle */}
            <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
              <ToggleButtonGroup
                value={role}
                exclusive
                onChange={(e, newRole) => newRole && setRole(newRole)}
                aria-label="user role"
                size="small"
              >
                <ToggleButton value="student" aria-label="student">
                  <Person sx={{ mr: 0.5 }} fontSize="small" />
                  {t('common.student')}
                </ToggleButton>
                <ToggleButton value="teacher" aria-label="teacher">
                  <School sx={{ mr: 0.5 }} fontSize="small" />
                  {t('common.teacher')}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Language Switcher */}
            <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
              <LanguageSwitcher />
            </Box>

            {/* Back button for register mode */}
            {isRegister && (
              <Box sx={{ mb: 2 }}>
                <IconButton onClick={handleSwitchMode} size="small">
                  <ArrowBack />
                </IconButton>
              </Box>
            )}

            <Typography
              component="h1"
              variant="h4"
              fontWeight="bold"
              gutterBottom
            >
              {isRegister
                ? t('login.registerTitle')
                : role === "teacher"
                ? t('login.teacherTitle')
                : t('login.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              {isRegister
                ? role === "teacher"
                  ? t('login.teacherRegisterSubtitle')
                  : t('login.registerSubtitle')
                : role === "teacher"
                ? t('login.teacherSubtitle')
                : t('login.subtitle')}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                margin="normal"
                required
                fullWidth
                label={t('login.phoneNumberLabel')}
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder={t('login.phoneNumberPlaceholder')}
                inputProps={{ maxLength: 10 }}
                autoFocus
                sx={{ mb: 2 }}
              />

              {/* 教师始终显示访问码 */}
              {role === "teacher" && (
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label={t('login.accessCodeLabel')}
                  type="password"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  placeholder={t('login.accessCodePlaceholder')}
                  sx={{ mb: 2 }}
                />
              )}

              {/* 注册时显示姓名 */}
              {isRegister && (
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label={t('login.nameLabel')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('login.namePlaceholder')}
                  sx={{ mb: 2 }}
                />
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                size="large"
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  fontSize: "1.1rem",
                  textTransform: "none",
                  borderRadius: 2,
                }}
              >
                {loading ? t('login.processing') : isRegister ? t('login.registerButton') : t('login.submitButton')}
              </Button>

              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Button
                  variant="text"
                  onClick={handleSwitchMode}
                  sx={{ textTransform: "none" }}
                >
                  {isRegister ? t('login.switchToLogin') : t('login.switchToRegister')}
                </Button>
              </Box>
            </Box>

            {/* Footer info */}
            <Box sx={{ mt: 4, textAlign: "center" }}>
              <Typography variant="caption" color="text.secondary">
                {t('login.terms')}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Login;
